import { Building2, FileText, Mail, Percent } from "lucide-react";
import { Controller, UseFormReturn } from "react-hook-form";
import { Button } from "../../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { Input } from "../../../../../components/ui/input";
import { Label } from "../../../../../components/ui/label";
import { Switch } from "../../../../../components/ui/switch";
import { Company } from "../types";
import { EditCredentialsFormData } from "../utils/validation-schemas";

interface EditCredentialsDialogProps {
  open: boolean;
  company: Company | null;
  form: UseFormReturn<EditCredentialsFormData, any, EditCredentialsFormData>;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
}

export function EditCredentialsDialog({
  open,
  company,
  form,
  onSave,
  onCancel,
  isSaving,
}: EditCredentialsDialogProps) {
  console.log(company);
  return (
    <Dialog open={open} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Editar Franquia - {company?.razaoSocial}</DialogTitle>
        </DialogHeader>

        <form onSubmit={onSave} className="space-y-6">
          <div className="p-4 rounded-lg bg-muted/50">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">
              Informações da Empresa
            </h3>
            <div className="space-y-2">
              <div>
                <span className="text-sm font-semibold">CNPJ:</span>{" "}
                <span className="text-sm">{company?.cnpj}</span>
              </div>
              <div>
                <span className="text-sm font-semibold">Razão Social:</span>{" "}
                <span className="text-sm">{company?.razaoSocial}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Controller
              name="apelido"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="space-y-2">
                  <Label htmlFor="edit-apelido">Apelido (OPS)</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="edit-apelido"
                      placeholder="Ex: rayosp"
                      {...field}
                      className="pl-9"
                    />
                  </div>
                  {fieldState.error && (
                    <p className="text-sm font-medium text-destructive">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="emailContato"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="space-y-2">
                  <Label htmlFor="edit-emailContato">Email(s) de Contato</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="edit-emailContato"
                      type="text"
                      placeholder="financeiro@empresa.com.br | outro@empresa.com.br"
                      {...field}
                      className="pl-9"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Para múltiplos emails, separe com | (pipe).
                  </p>
                  {fieldState.error && (
                    <p className="text-sm font-medium text-destructive">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>

          <div className="space-y-4">
            <Controller
              name="franquiaUuid"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="space-y-2">
                  <Label htmlFor="edit-franquiaUuid">Franquia UUID</Label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="edit-franquiaUuid"
                      placeholder="8d03027b-5236-4533-8e9e-..."
                      {...field}
                      className="pl-9"
                    />
                  </div>
                  {fieldState.error && (
                    <p className="text-sm font-medium text-destructive">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="redutor"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="space-y-2">
                  <Label htmlFor="edit-redutor">Redutor</Label>
                  <div className="relative">
                    <Percent className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="edit-redutor"
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      placeholder="1.00"
                      {...field}
                      onChange={(e) => field.onChange(e.target.valueAsNumber || e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  {fieldState.error && (
                    <p className="text-sm font-medium text-destructive">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />

            <div className="space-y-2">
              <Label>Formato</Label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  value={company?.formato || ""}
                  disabled
                  className="pl-9 bg-muted"
                />
              </div>
            </div>

            <Controller
              name="nfsAtivo"
              control={form.control}
              render={({ field }) => (
                <div className="flex items-center justify-between p-4 rounded-lg border bg-background">
                  <Label
                    htmlFor="edit-nfsAtivo"
                    className="text-base cursor-pointer"
                  >
                    NFS (Nota Fiscal de Serviço) Ativo
                  </Label>
                  <Switch
                    id="edit-nfsAtivo"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </div>
              )}
            />

            <Controller
              name="nfsSrAtivo"
              control={form.control}
              render={({ field }) => (
                <div className="flex items-center justify-between p-4 rounded-lg border bg-background">
                  <Label
                    htmlFor="edit-nfsSrAtivo"
                    className="text-base cursor-pointer"
                  >
                    NFS Saldo Remanescente Ativo
                  </Label>
                  <Switch
                    id="edit-nfsSrAtivo"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </div>
              )}
            />

            <Controller
              name="ofertaAtiva"
              control={form.control}
              render={({ field }) => (
                <div className="flex items-center justify-between p-4 rounded-lg border bg-background">
                  <div className="space-y-1">
                    <Label
                      htmlFor="edit-ofertaAtiva"
                      className="text-base cursor-pointer"
                    >
                      Envio de Ofertas Ativo
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Desligado, a franquia fica fora do envio de ofertas do
                      checkout e da busca de oferta pelo WhatsApp. Ofertas já
                      enviadas ainda podem ser aceitas.
                    </p>
                  </div>
                  <Switch
                    id="edit-ofertaAtiva"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </div>
              )}
            />
          </div>
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSaving}
          >
            CANCELAR
          </Button>
          <Button type="button" onClick={onSave} disabled={isSaving}>
            {isSaving ? "Atualizando..." : "ATUALIZAR"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
